// กราฟ order รายชั่วโมงของวันที่เลือก + เส้นความจุ/ชม. ตาม what-if — วาดด้วย div ทรงเดียวกับหน้า Analytics (ไม่มี chart lib)
import React from 'react';
import { fmtNum } from '../../../utils/formatMoney';
import { fmtCap } from './capacityMath';

interface HourlyChartProps {
  /** 24 ช่อง index 0..23 ชั่วโมงไทย */
  hourly: number[];
  /** Infinity = ไม่จำกัด (ไม่วาดเส้น) */
  capacityPerHour: number;
  isToday: boolean;
  nowHour: number;
  peakHour: number | null;
}

const LEGEND_SWATCH = 'w-2.5 h-2.5 rounded-sm inline-block';

const HourlyChart: React.FC<HourlyChartProps> = ({ hourly, capacityPerHour, isToday, nowHour, peakHour }) => {
  const maxBar = Math.max(0, ...hourly);
  const cap = Number.isFinite(capacityPerHour) ? capacityPerHour : null;
  // ยืดแกนให้เห็นเส้นความจุ แต่ไม่เกิน 3 เท่าของแท่งสูงสุด (ไม่งั้นแท่งแบนจนอ่านไม่ออก)
  const axisMax = Math.max(1, maxBar, cap !== null ? Math.min(cap, Math.max(maxBar, 1) * 3) : 0);
  const barHeight = (v: number) => (v > 0 ? Math.max((v / axisMax) * 100, 2) : 0);
  const lineBottom = cap !== null ? Math.min((cap / axisMax) * 100, 100) : null;
  const overflow = cap !== null && cap > axisMax;

  return (
    <div>
      <div className="relative h-44 px-2">
        {lineBottom !== null && cap !== null && (
          <div
            className="absolute left-2 right-2 border-t-2 border-dashed border-red-400 dark:border-red-500 pointer-events-none z-10"
            style={{ bottom: `${lineBottom}%` }}
          >
            <span className="absolute right-0 -top-4 text-[10px] text-red-500 dark:text-red-400 bg-white/80 dark:bg-slate-800/80 px-1 rounded">
              ความจุ/ชม. {fmtNum(cap)}{overflow ? ' (เกินสเกล)' : ''}
            </span>
          </div>
        )}
        <div className="flex items-end gap-[3px] h-full">
          {hourly.map((v, hour) => {
            const future = isToday && hour > nowHour;
            const isPeak = peakHour === hour && v > 0;
            const over = cap !== null && v > cap;
            const barClass = future
              ? 'bg-gray-100 dark:bg-slate-700 opacity-60'
              : over
                ? 'bg-gradient-to-t from-red-500 to-red-300'
                : isPeak
                  ? 'bg-gradient-to-t from-orange-500 to-orange-300 ring-2 ring-orange-400 ring-offset-1 dark:ring-offset-slate-800'
                  : v > 0
                    ? 'bg-gradient-to-t from-blue-500 to-blue-300 hover:from-blue-600 hover:to-blue-400'
                    : 'bg-gray-100 dark:bg-slate-700';
            return (
              <div key={hour} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                <div className="absolute bottom-full mb-1 hidden group-hover:block z-20">
                  <div className="bg-gray-800 dark:bg-slate-900 text-white text-[10px] rounded-lg px-2 py-1.5 whitespace-nowrap shadow-lg border border-slate-700">
                    <div className="font-bold">{String(hour).padStart(2, '0')}:00</div>
                    <div>ออเดอร์: {fmtNum(v)}</div>
                    <div className="text-red-300">ความจุ: {fmtCap(capacityPerHour)}</div>
                    {cap !== null && cap > 0 && <div className="text-yellow-300">{Math.round((v / cap) * 100)}% ของความจุ</div>}
                    {future && <div className="text-gray-400">ยังไม่ถึง</div>}
                  </div>
                </div>
                <div
                  className={`w-full rounded-t transition-all duration-300 cursor-pointer ${barClass}`}
                  style={{ height: `${barHeight(v)}%`, minHeight: v > 0 ? '4px' : '0px' }}
                />
                <span className={`text-[9px] mt-1 ${isPeak ? 'text-orange-600 dark:text-orange-400 font-bold' : 'text-gray-400 dark:text-slate-500'}`}>
                  {hour % 3 === 0 ? String(hour).padStart(2, '0') : ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 mt-3 text-[10px] text-gray-400 dark:text-slate-500 px-2">
        <span className="flex items-center gap-1"><span className={`${LEGEND_SWATCH} bg-gradient-to-t from-blue-500 to-blue-300`} /> ออเดอร์</span>
        <span className="flex items-center gap-1"><span className={`${LEGEND_SWATCH} bg-gradient-to-t from-orange-500 to-orange-300 ring-1 ring-orange-400`} /> ชั่วโมงพีค</span>
        <span className="flex items-center gap-1"><span className={`${LEGEND_SWATCH} bg-gradient-to-t from-red-500 to-red-300`} /> เกินความจุ/ชม.</span>
        <span className="flex items-center gap-1"><span className="w-4 border-t-2 border-dashed border-red-400 inline-block" /> ความจุ/ชม. ({fmtCap(capacityPerHour)})</span>
        {isToday && <span className="flex items-center gap-1"><span className={`${LEGEND_SWATCH} bg-gray-100 dark:bg-slate-700`} /> ยังไม่ถึง</span>}
      </div>
    </div>
  );
};

export default HourlyChart;
